/**
 * @file AddEditUniversityModal.js
 * @description Modal component for adding and editing university details.
 *              Handles form input, validation, and API communication for university CRUD operations.
 * @created June 2025
 */

import React, { Component } from "react";
import { Modal, Button, Form, Input, message } from "antd";
import { addUpdateUniversity } from "../../services";
import { showSuccessNotification } from "../../helpers";

const FormItem = Form.Item;

/**
 * @class AddEditUniversityModal
 * @extends Component
 * @description Modal component for adding/editing university information
 */
class AddEditUniversityModal extends Component {
  /**
   * @constructor
   * @param {Object} props - Component props
   * @param {boolean} props.showModal - Controls modal visibility
   * @param {Function} props.toggleModal - Function to toggle modal visibility
   * @param {Object} props.selectedUniversity - University data for editing (null for add mode)
   * @param {Function} props.callback - Callback function to execute after successful save/update
   */
  constructor(props) {
    super(props);
    this.state = {
      universityName: "", // Stores the university name input
      rank: "", // Stores the university rank input
      formProcessing: false // Controls loading state during form submission
    };
  }

  /**
   * @lifecycle
   * @description Populate form fields if editing an existing university
   */
  componentDidMount() {
    const { selectedUniversity } = this.props;
    if (selectedUniversity) {
      this.setState({
        universityName: selectedUniversity.name,
        rank: selectedUniversity.rank
      });
    }
  }

  /**
   * @function handleChange
   * @description Generic handler for form input changes
   * @param {string} name - Name of the state field to update
   * @param {string} value - New value for the field
   */
  handleChange = (name, value) => {
    this.setState({ [name]: value });
  };

  /**
   * @function handleSaveUniversity
   * @description Validates form and submits data to create/update university
   * @returns {boolean} False if validation fails
   */
  handleSaveUniversity = () => {
    const { selectedUniversity } = this.props;
    const { universityName, rank } = this.state;

    // Validate required fields
    if (!universityName) {
      message.error("Please enter university name");
      return false;
    }

    // Prepare request parameters
    const params = {
      name: universityName,
      rank: rank || ''
    };

    // Add ID if updating existing university
    if (selectedUniversity && selectedUniversity.id) {
      params.id = selectedUniversity.id;
    }
    
    this.setState({ formProcessing: true });
    addUpdateUniversity(params)
      .then(response => {
        if (response.data.success) {
          showSuccessNotification(
            `University ${
              selectedUniversity && selectedUniversity.id ? "updated" : "added"
            } successfully!`
          );
          this.props.callback(); // Refresh parent component
          setTimeout(() => {
            this.props.toggleModal(); // Close modal after short delay
          }, 200);
        }
      })
      .finally(() => {
        this.setState({ formProcessing: false });
      });
  };

  /**
   * @function render
   * @description Renders the modal with university form
   * @returns {JSX.Element} Rendered component
   */
  render() {
    const { showModal, toggleModal, selectedUniversity } = this.props;
    const {
      universityName,
      rank,
      formProcessing
    } = this.state;
    
    return (
      <Modal
        visible={showModal}
        onCancel={toggleModal}
        title={selectedUniversity ? "Update University" : "Add University"}
        maskClosable={false}
        footer={null}
      >
        <Form>
          <FormItem label="University Name">
            <Input
              value={universityName}
              onChange={e => this.handleChange("universityName", e.target.value)}
            />
          </FormItem>
          <FormItem label="Rank">
            <Input
              value={rank}
              onChange={e => this.handleChange("rank", e.target.value)}
              type="number"
              placeholder="Enter university rank"
            />
          </FormItem>
          <div>
            <Button
              type="primary"
              onClick={this.handleSaveUniversity}
              loading={formProcessing}
              style={{ marginRight: 10 }}
            >
              {selectedUniversity ? "Update" : "Save"}
            </Button>
            <Button type="link" onClick={toggleModal}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>
    );
  }
}

export default AddEditUniversityModal;
